/* eslint-disable no-nested-ternary */
import { Injectable } from '@angular/core';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { DateTimePresentationPipe } from 'src/app/modules/core/pipes/date-time-presentation.pipe';
import { ShortDateTimePresentationPipe } from 'src/app/modules/core/pipes/date-time-short-presentation.pipe';
import { DateTimeTextPipe } from 'src/app/modules/core/pipes/date-time-text.pipe';
import { FullDateTimePresentationPipe } from 'src/app/modules/core/pipes/full-date-time-presentation.pipe';
import { FullTimePresentationPipe } from 'src/app/modules/core/pipes/full-time-presentation.pipe';
import { TimePresentationPipe } from 'src/app/modules/core/pipes/time-presentation.pipe';
import { COMPANIES_ID } from '../../enumerables/companies-id';
import { HCM_STATE_COLORS } from '../../enumerables/hcm-state-colors';
import { STATE_COLORS } from '../../enumerables/state-colors';
import { UNITS } from '../../enumerables/units';
import { ITableTooltip } from '../../interfaces/table/table-badge';
import { ITableDataColorConfig } from '../../interfaces/table/table-data-color-config';
import { ITableDataDateConfig } from '../../interfaces/table/table-data-date-config';
import { ITableDataImageConfig } from '../../interfaces/table/table-data-image-config';
import { ITableDataOption } from '../../interfaces/table/table-data-option';
import { ITableDataParams } from '../../interfaces/table/table-data-params';
import { ITableDataReplaceConfig } from '../../interfaces/table/table-data-replace-config';
import { ITableHeaderOption } from '../../interfaces/table/table-header-option';
import { ITableHeaderParams } from '../../interfaces/table/table-header-params';
import { CacheService } from '../cache.service';
import { LanguageService } from '../parameters';
import { isDefined } from '../utils/utilities';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TableConfigService {
  public dateColumns: string[];
  public subscriptionDataSet: Subject<boolean> = new Subject<boolean>();

  constructor(
    private languageService: LanguageService,
    private cacheService: CacheService,
    private timeFullPresentation: FullTimePresentationPipe,
    private datePresentation: DatePresentationPipe,
    private fullTimePipe: FullTimePresentationPipe,
  ) {
    this.dateColumns = [];
  }

  /**
   * Function to set the table main configuration
   *
   * @param headersParam Params for headers
   * @param datasetParam Params for dataset
   * @returns table main config for headers and dataset
   */
  public setTableMainConfig(
    headersParam: ITableHeaderParams,
    datasetParam: ITableDataParams,
  ): { header: ITableHeaderOption[]; dataset: ITableDataOption[][] } {
    const header = this.setHeaderConfig(headersParam);
    const dataset = this.setDatasetConfig(datasetParam);
    return { header, dataset };
  }

  /**
   * Function to set the headers to table
   *
   * @param params Parameters to create the headers
   */
  public setHeaderConfig(params: ITableHeaderParams): ITableHeaderOption[] {
    const headerOption: ITableHeaderOption[] = [];

    if (params && params.data && params.data.length > 0 && params.data[0]) {
      const headers = Object.keys(params.data[0]);
      for (const header of headers) {
        const isSortable =
          (typeof params.sortables === 'boolean' && params.sortables === true) ||
          (typeof params.sortables === 'object' && params.sortables.includes(header));

        const isSearchable =
          (typeof params.searchables === 'boolean' && params.searchables === true) ||
          (typeof params.searchables === 'object' && params.searchables.includes(header));

        const isLargeColumn = params.largeColumns && params.largeColumns.includes(header);
        const isExcludeColumn = params.excludeColumns && params.excludeColumns.includes(header);
        const customLargeColumnConfig = params.customLargeColumns
          ? params.customLargeColumns.find((value) => value.field === header)
          : null;

        const isTranslatable = isDefined(params && params.translatable) ? params.translatable : true;

        const sortColumn = params.sortField && params.sortField.find((value) => value.field === header);

        let visibleName = header;

        if (params.replaceColumnName) {
          const foundReplaceColumnName = params.replaceColumnName.find((option) => header === option.prevName);

          if (foundReplaceColumnName) {
            visibleName = foundReplaceColumnName.newName;
          }
        }
        const tooltip = params.tooltip ? params.tooltip.find((value) => value.field === header) : null;

        headerOption.push({
          visibleName,
          value: header,
          isSearchable,
          isSortable,
          isLargeColumn,
          customLargeColumnConfig,
          isExcludeColumn,
          isTranslatable,
          tooltip,
          sortBy: sortColumn ? sortColumn.sortBy : null,
        });
      }
    }
    return headerOption;
  }

  /**
   * Function to set dataset to table
   *
   * @param params Params to create the dataset
   */
  public setDatasetConfig(params: ITableDataParams): ITableDataOption[][] {
    const totalDataset: ITableDataOption[][] = [];
    let dataset: ITableDataOption[] = [];
    if (params.tableData) {
      params.tableData.forEach((data, rowIndex) => {
        dataset = [];
        Object.keys(data ?? []).forEach((key) => {
          const dateConfig = params.dateConfig ? params.dateConfig.find((value) => value.field === key) : null;
          const imageConfig = params.imageConfig ? params.imageConfig.find((value) => value.field === key) : null;
          const buttonConfig = params.buttonConfig ? params.buttonConfig.find((value) => value.field === key) : null;
          const colorConfig = params.colorConfig ? params.colorConfig.find((value) => value.field === key) : null;
          const replaceConfig = params.replaceConfig ? params.replaceConfig.find((value) => value.field === key) : null;
          const isLargeColumn = params.largeColumns && params.largeColumns.includes(key);
          const customLargeColumnConfig = params.customLargeColumns ? params.customLargeColumns.find((value) => value.field === key) : null;
          const isclicked = params.clickConfig ? params.clickConfig.find((value) => value.field === key) : null;
          const isExcludeColumn = params.excludeColumns && params.excludeColumns.includes(key);
          const status = params && isDefined(params.status) && key === 'status' ? params.status : null;
          const isActive = params && isDefined(params.isActive) && key === 'isActive' ? params.isActive : null;
          const deactivateTitle = params.deactivateTitle ? params.deactivateTitle.find((value) => value.field === key) : null;
          const badges = params.badge ? params.badge.find((value) => value.field === key) : null;
          const isActiveTooltip = params && isDefined(params.isActiveTooltip) ? params.isActiveTooltip : null;
          const tooltipConfig = params.tooltipColumn ? params.tooltipColumn.find((value) => value.field === key) : null;
          const textColorConfig = params.textColorConfig
            ? params.textColorConfig.find((value, index) => value.field === key && index === rowIndex)
            : null;

          const tooltipDynamicConfig = params.tooltipDynamicConfig
            ? params.tooltipDynamicConfig.find((value, index) => value.field === key && index === rowIndex)
            : null;

          let extraData = null;
          if (params.extraData && key === params.extraData.field && params.extraData.data) {
            extraData = data[params.extraData.data];
          }
          dataset.push({
            key,
            extraData,
            value: this.getDatasetValue(data, key, dateConfig, replaceConfig),
            isLargeColumn,
            buttonConfig,
            customLargeColumnConfig,
            clickConfig: isclicked,
            isExcludeColumn,
            imageConfig:
              imageConfig && imageConfig.isImageStatus
                ? this.getImageConfig(imageConfig, data[key], data.parentStatusImagePrefix, data.machineIcon)
                : imageConfig
                ? this.getImageConfig(imageConfig, data[key])
                : null,
            colorConfig:
              colorConfig && colorConfig.isColorStatus
                ? this.getColorConfig(colorConfig, data.parentStatus || data.status, data.statusColor)
                : colorConfig
                ? this.getColorConfig(colorConfig)
                : null,
            status,
            textColorConfig,
            isActive,
            deactivateTitle,
            rowHover: params.enableRowHover,
            badge: badges,
            tooltipDynamicConfig,
            isActiveTooltip,
            tooltipColumn:
              tooltipConfig && tooltipConfig.field
                ? this.getTooltipConfig(tooltipConfig, data)
                : tooltipConfig
                ? this.getTooltipConfig(tooltipConfig, data)
                : null,
          });
        });

        totalDataset.push(dataset);
      });
    }
    return totalDataset;
  }

  /**
   * Function to get the tooltip config
   *
   * @param getTooltipConfig tooltip config to field
   */
  private getTooltipConfig(tooltipConfig: ITableTooltip, key: any): ITableTooltip {
    const formatDate = this.datePresentation.transform(key.since);
    const formatTime = this.timeFullPresentation.transform(key.since);
    const unitsType = this.getUnitTooltip(key.machineType);
    const notRegister = this.languageService.message('not-register-information');
    const state = this.languageService.message('in-state');
    const since = this.languageService.message('since');
    const drillings = this.languageService.message('drillings');
    const metersDrill = this.languageService.message('drillings_mts');
    const first = this.languageService.message('first');
    const last = this.languageService.message('last');
    const bucketProduction = this.languageService.message('bucket_production_summary');
    const loopProduction = this.languageService.message('loop_production_summary');
    const drill = this.languageService.message('drill_summary');

    const tooltipMappings: { [key: string]: () => void } = {
      status: () => {
        tooltipConfig.tooltipText =
          key.status === '-' ? notRegister : `${state}: ${key.status}\n${since}: ${formatDate}\n${formatTime} hrs`;
      },
      lastLogIn: () => {
        const formatedDate = new ShortDateTimePresentationPipe().transform(key.lastLogInTooltip);
        tooltipConfig.tooltipText = key.lastLogIn === '-' ? notRegister : `${formatedDate} hrs`;
      },
      productionTotal: () => {
        if (unitsType === 'Perforación') {
          const productionCount = `\n${drillings}: ${key.productionCount}`;
          tooltipConfig.tooltipText = `${metersDrill} ${key.productionTotal.replace('MTS', ' ')}${productionCount}`;
        }
        if (unitsType === 'Baldada') {
          if (key.productionCount === 0 || key.productionCount > 1) {
            tooltipConfig.tooltipText = ` ${bucketProduction}s : ${key.productionCount}`;
          } else tooltipConfig.tooltipText = `${bucketProduction} : ${key.productionCount}`;
        }
        if (unitsType === 'Vuelta') {
          if (key.productionCount === 0 || key.productionCount > 1) {
            tooltipConfig.tooltipText = ` ${loopProduction}s : ${key.productionCount}`;
          } else tooltipConfig.tooltipText = `${loopProduction} : ${key.productionCount}`;
        }
      },
      firstProductionTime: () => {
        if (unitsType === 'Perforación') {
          tooltipConfig.tooltipText =
            key.firstProductionTime === '-'
              ? notRegister
              : `${first} ${drill}:\n${this.fullTimePipe.transform(key.firstProductionTooltip)} hrs`;
        }
        if (unitsType === 'Baldada') {
          tooltipConfig.tooltipText =
            key.firstProductionTime === '-'
              ? notRegister
              : `${first} ${bucketProduction}:\n${this.fullTimePipe.transform(key.firstProductionTooltip)} hrs`;
        }
        if (unitsType === 'Vuelta') {
          tooltipConfig.tooltipText =
            key.firstProductionTime === '-'
              ? notRegister
              : `${first} ${loopProduction}:\n${this.fullTimePipe.transform(key.firstProductionTooltip)} hrs`;
        }
      },
      lastProductionTime: () => {
        if (unitsType === 'Perforación') {
          tooltipConfig.tooltipText =
            key.lastProductionTime === '-'
              ? notRegister
              : `${last} ${drill}:\n${this.fullTimePipe.transform(key.lastProductionTooltip)} hrs`;
        }
        if (unitsType === 'Baldada') {
          tooltipConfig.tooltipText =
            key.lastProductionTime === '-'
              ? notRegister
              : `${last} ${bucketProduction}:\n${this.fullTimePipe.transform(key.lastProductionTooltip)} hrs`;
        }
        if (unitsType === 'Vuelta') {
          tooltipConfig.tooltipText =
            key.lastProductionTime === '-'
              ? notRegister
              : `${last} ${loopProduction}:\n${this.fullTimePipe.transform(key.lastProductionTooltip)} hrs`;
        }
      },
      lastZone: () => {
        tooltipConfig.tooltipText = key.lastZone === '-' ? notRegister : `${key.lastZone} - ${key.lastUbication}`;
      },
      operator: () => {
        tooltipConfig.tooltipText = key.operatorTooltip === '-' ? notRegister : ` ${key.operatorTooltip}`;
      },
    };

    const mappingFunction = tooltipMappings[tooltipConfig.field];
    if (mappingFunction) {
      mappingFunction();
    }

    return { ...tooltipConfig };
  }

  /**
   * Function to get units
   * @param keyMachineType
   * @returns
   */
  public getUnitTooltip(keyMachineType: string): string {
    const unitMapping: { [key: string]: string } = {
      LHD: 'Baldada',
      Jumbo: 'Perforación',
      Truck: 'Vuelta',
    };

    return unitMapping[keyMachineType] || '-';
  }

  /**
   * Function to get the format date presentation
   *
   * @param format Format date
   */
  private getDateFormatted(dateConfig: ITableDataDateConfig, value: string): any {
    let pipe = null;
    if (!this.dateColumns.includes(dateConfig.field)) {
      this.dateColumns.push(dateConfig.field);
    }
    switch (dateConfig.format) {
      case 'date':
        pipe = new DatePresentationPipe();
        return pipe.transform(value);

      case 'date-time':
        pipe = new DateTimePresentationPipe();
        return pipe.transform(value);

      case 'full-date-time':
        pipe = new FullDateTimePresentationPipe();
        return pipe.transform(value);

      case 'time':
        pipe = new TimePresentationPipe();
        return pipe.transform(value);

      case 'full-time':
        pipe = new FullTimePresentationPipe();
        return pipe.transform(value);

      case 'dateTimeText':
        pipe = new DateTimeTextPipe();
        return pipe.transform(value);

      case 'date-time-short':
        pipe = new ShortDateTimePresentationPipe();
        return pipe.transform(value);
    }
  }

  /**
   * Function to replace parameters to image config
   *
   * @param imageConfig Image config to field
   */
  private getImageConfig(
    imageConfig: ITableDataImageConfig,
    value: string,
    parentStatusImagePrefix?: string,
    machineIcon?: string,
  ): ITableDataImageConfig {
    const newConfig = { ...imageConfig };
    let valueToReplace;
    if (imageConfig.fieldReplace) {
      if (typeof value === 'boolean') {
        valueToReplace = String(value);
      } else {
        valueToReplace = value.toLocaleLowerCase();
      }
      newConfig.imageType = imageConfig.imageType.replace('[field]', valueToReplace);
    }

    if (imageConfig.fieldReplaceName) {
      if (typeof value === 'boolean') {
        valueToReplace = String(value);
      } else {
        valueToReplace = value.toLocaleLowerCase();
      }
      newConfig.imageName = imageConfig.imageName.replace('[field]', valueToReplace);
    }
    if (imageConfig.isImageStatus) {
      parentStatusImagePrefix =
        parentStatusImagePrefix && parentStatusImagePrefix !== '-' ? parentStatusImagePrefix.split(' ').join('-').toLowerCase() : null;
      const statusSide = machineIcon ? `status-side-${machineIcon.toLocaleLowerCase()}` : `status-side`;
      newConfig.imageName = parentStatusImagePrefix ? `${statusSide}-${parentStatusImagePrefix}` : `${statusSide}-sin-estado`;
    }
    return { ...newConfig };
  }

  /**
   * Function to get the color config
   *
   * @param colorConfig Color config to field
   */
  private getColorConfig(colorConfig: ITableDataColorConfig, parentStatus?: string, statusColor?: string): ITableDataColorConfig {
    let { color } = colorConfig;
    const colorColumnFirst = colorConfig;

    if (colorConfig.isColorStatus) {
      color = parentStatus.split(' ').join('-').toLowerCase();
    }
    colorConfig.color = STATE_COLORS[color] ? STATE_COLORS[color] : STATE_COLORS.default;

    const companyId = this.cacheService.local('session').user.company.id;
    const isHcm = companyId === COMPANIES_ID.HCM;

    const colors = isHcm ? HCM_STATE_COLORS : STATE_COLORS;

    colorConfig.color = colors[color] ? colors[color] : colors.default;

    if (colorConfig.statusColor) {
      colorConfig.color = statusColor;
    }

    if (colorConfig.isBold) {
      return { ...colorConfig };
    }
    if (colorColumnFirst) {
      colorConfig.color = isHcm ? HCM_STATE_COLORS['demora-operativa'] : STATE_COLORS.demora;
    }

    return { ...colorConfig };
  }

  /**
   * Function to replace values
   *
   * @param replaceConfig Config to replace fields
   * @param value Current value to replace
   * @param machineType optional machine type to get unit
   */
  private getReplacedValue(replaceConfig: ITableDataReplaceConfig, value: string, data: any[]): string {
    let newValue = value;
    enum UNITS_SUMMARY {
      jumbo = 'MTS',
      lhd = 'TONS',
      truck = 'TONS',
      default = '-',
    }

    if (replaceConfig.isUnit) {
      if (replaceConfig.isSummary) {
        newValue = replaceConfig.value;
        const unit = UNITS_SUMMARY[data[replaceConfig.fieldToReplace].toLowerCase()]
          ? UNITS_SUMMARY[data[replaceConfig.fieldToReplace].toLowerCase()]
          : UNITS_SUMMARY.default;
        newValue = unit !== '-' ? `${value} ${unit}` : '-';
      } else {
        newValue = replaceConfig.value;
        const unit = UNITS[data[replaceConfig.fieldToReplace].toLowerCase()]
          ? UNITS[data[replaceConfig.fieldToReplace].toLowerCase()]
          : UNITS.default;
        newValue = unit !== '-' ? `${value} ${this.languageService.message(unit)}` : '-';
        const newValue2 = newValue.split(' ');
        if (newValue2.length > 2) {
          newValue = `${newValue2[0].toString()} ${newValue2[1].toString()}`;
        }
      }
    } else if (
      !replaceConfig.specificValueToReplace ||
      (replaceConfig.specificValueToReplace && replaceConfig.specificValueToReplace === String(value))
    ) {
      newValue = replaceConfig.replaceText;
      // Replace the [value] with dataset value
      newValue = newValue.replace('[value]', value);

      // Replace [field] with a field of dataset
      newValue = newValue.replace('[field]', data[replaceConfig.fieldToReplace]);
    }
    return newValue;
  }

  /**
   * Function to get dataset value
   *
   * @param value Value to use
   * @param config Config optional
   */
  private getDatasetValue(
    data: any[],
    key: string,
    dateConfig: ITableDataDateConfig = null,
    replaceConfig: ITableDataReplaceConfig = null,
  ): string {
    if (isDefined(data[key])) {
      data[key] = dateConfig ? this.getDateFormatted(dateConfig, data[key]) : data[key];
      data[key] = replaceConfig ? this.getReplacedValue(replaceConfig, data[key], data) : data[key];
    } else {
      data[key] = data[key] === 0 ? '0' : '-';
    }

    return data[key];
  }

  /**
   * Function to know if a column is a date field
   *
   * @param column Column key to search
   */
  public isDateColumn(column: string): boolean {
    const found = this.dateColumns.find((value) => value === column);
    return !!found;
  }

  /**
   * Function to set the subscription data set end
   */
  public subcriptionDataSet(): void {
    this.subscriptionDataSet.next(true);
  }
}
